import { defineStore } from 'pinia'
import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import findIndex from 'lodash/findIndex'
import extend from 'lodash/extend'
import { HTTP_METHODS } from '~/definitions/enums'
import type {
  BpUser,
  BpUserAddress,
  StoreResponse,
  TwAlertOptions,
} from '~/definitions/AllDefinitions'
import { useCartStore } from '~/store/CartStore'
import { useMainStore } from '~/store/MainStore'
import { useOrderStore } from '~/store/OrderStore'
import { defaultError } from '~/classes/ErrorHandlers'
import type { UserStoreInterface } from '~/definitions/StateDefinitions'

export const useUserStore = defineStore('user-store',
  {
    persist: {
      pick: ['user', 'theme'],
    },
    state: (): UserStoreInterface => ({
      user: null,
      primaryLocationId: 0,
      theme: '',
    }),
    getters: {
      currentUser: state => state.user,
      userId: state => state.user && state.user.id,
      loggedIn: state => !!(state.user && state.user.id),
      defaultLocationId: state => state.user && state.user.defaultLocationId,
    },
    actions: {
      baseUrl() {
        const store = useMainStore()
        return store.accountId
          ? `/api/${store.accountId}/users`
          : null
      },
      clear() {
        this.user = null
        this.primaryLocationId = 0
        const cartStore = useCartStore()
        cartStore.clear()
        const orderStore = useOrderStore()
        orderStore.clear()
      },
      async createAddress(body: any): Promise<StoreResponse> {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const data = await $fetch(`${url}/me/addresses`, {
            method: 'post',
            body,
          })
          return {
            success: true,
            data: data as BpUserAddress,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            data: null,
            error: formatError(err),
          }
        }
      },
      async createPaymentMethod(card: any): Promise<StoreResponse> {
        try {
          const data = await $fetch(`${this.baseUrl()}/me/payment-methods/`, {
            method: HTTP_METHODS.POST,
            body: {
              ...card,
            },
          })
          return {
            success: true,
            data,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            data: null,
            error: formatError(err),
          }
        }
      },
      async deleteAccount() {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          await $fetch(url, {
            method: 'delete',
          })
          return {
            success: true,
            error: null,
            data: null,
          }
        } catch (err: any) {
          this.clear()
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async deleteAddress(uuid: string) {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          await $fetch(`${url}/me/addresses/${uuid}`, {
            method: 'delete',
          })
          return {
            success: true,
            error: null,
            data: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async getPaymentMethods(): Promise<StoreResponse> {
        try {
          const url = this.baseUrl()
          if (!url) {
            return defaultError
          }
          const data = await $fetch(`${url}/me/payment-methods`, {
            method: HTTP_METHODS.GET,
          })
          return {
            success: true,
            data,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async getAddresses(): Promise<StoreResponse> {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const data = await $fetch(`${url}/me/addresses`, {
            method: 'get',
          })
          return {
            error: null,
            data: data as BpUserAddress[],
            success: true,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            error: formatError(err),
            data: null,
            success: false,
          }
        }
      },
      async getUser(id = 0): Promise<StoreResponse> {
        try {
          const url = this.baseUrl()
          if (!url) {
            return defaultError
          }
          const path = id === 0 ? '/me' : `/${id}`
          const data = await $fetch(`${url}${path}`, {
            method: HTTP_METHODS.GET,
          })
          return {
            success: true,
            data,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          if (err.statusCode === 401) {
            this.user = null
          }
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async login(body: any): Promise<StoreResponse> {
        const store = useMainStore()
        if (!store.accountId) {
          return defaultError
        }
        try {
          const data = await $fetch('/api/auth/login', {
            method: 'POST',
            body: { accountId: store.accountId, ...body },
          })
          return this.loginUser(data as any)
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async logout() {
        const store = useMainStore()
        if (!store.accountId) {
          this.clear()
          return defaultError
        }
        try {
          await $fetch('/api/auth/logout', {
            method: 'POST',
            body: { accountId: store.accountId },
          })
          this.clear()
          if (import.meta.client) {
            try {
              const { $posthog } = useNuxtApp()
              $posthog.reset()
            } catch {
              // Nuxt context unavailable (e.g., in tests)
            }
          }
          return {
            success: true,
            error: null,
            data: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async loginUser(ret: { user: BpUser }) {
        if (ret.user) {
          await this.setUser(ret.user)
          return {
            success: true,
            data: ret.user,
            error: null,
          }
        }
        return {
          success: false,
          data: null,
          error: {
            display: true,
            headline: 'ERROR_CREATING_ACCOUNT',
            type: 'error',
          } as TwAlertOptions,
        }
      },
      async register(body: any) {
        const store = useMainStore()
        if (!store.accountId) {
          this.clear()
          return defaultError
        }
        try {
          const data = await $fetch('/api/auth/register', {
            method: 'POST',
            body: {
              accountId: store.accountId,
              ...body,
              theme: this.theme,
            },
          })
          return this.loginUser(data as any)
        } catch (err: any) {
          this.clear()
          const formatError = useFormatError()
          return {
            success: false,
            data: null,
            error: formatError(err),
          }
        }
      },
      async resetPassword(body: any): Promise<StoreResponse> {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const data = await $fetch(`${url}/passwords`, {
            method: 'put',
            body,
          })
          return { error: null, data, success: true }
        } catch (err: any) {
          const formatError = useFormatError()
          return { success: false, error: formatError(err), data: null }
        }
      },
      async requestPasswordReset(body: any): Promise<StoreResponse> {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const res = await $fetch(`${url}/passwords`, {
            method: 'post',
            body,
          })
          return { success: true, data: res, error: null }
        } catch (e: any) {
          const formatError = useFormatError()
          return { success: false, data: null, error: formatError(e) }
        }
      },
      async setTheme(theme: string) {
        if (theme !== this.theme) {
          this.theme = clone(theme)
          if (theme.length && this.user && this.user.id && theme !== this.user.theme) {
            await this.updateUser({ theme: clone(theme) })
          }
        }
      },
      async setColorMode() {
        let colorMode
        try {
          colorMode = useColorMode()
        } catch {
          return
        }
        const theme = this.theme.toLowerCase()
        switch (theme) {
          case 'light':
          case 'dark':
            colorMode.preference = theme
            break
          default:
            // No explicit user preference — let @nuxtjs/color-mode handle
            // system detection via its built-in 'system' mode. This respects
            // prefers-color-scheme and avoids the SSR bug where import.meta.client
            // is false, causing isDarkModePreferred to always be false, which
            // then persists 'light' and overrides the system dark mode on
            // subsequent client-side navigations.
            colorMode.preference = 'system'
            break
        }
      },
      async setUser(user: BpUser | null) {
        this.user = user
        if (user) {
          const mainStore = useMainStore()
          const orderStore = useOrderStore()
          if (!orderStore.order.addressOne && user.addresses && user.addresses.length) {
            const primaryAddresses = user.addresses.filter((address) => {
              return address.isPrimaryDelivery
            })
            if (primaryAddresses.length) {
              const primaryAddress = cloneDeep(primaryAddresses[0]) as any
              delete primaryAddress.id
              orderStore.order = extend(orderStore.order, primaryAddress)
            }
          }
          if (!mainStore.currentLocation && user.defaultLocationId) {
            if (mainStore.currentLocations.length) {
              const idx = findIndex(mainStore.currentLocations, (o) => {
                return o.id === user.defaultLocationId
              })
              if (idx > 0) {
                this.primaryLocationId = clone(mainStore.currentLocations[idx].id)
                await mainStore.getLocation(this.primaryLocationId)
              }
            }
          }
          if (user.theme) {
            await this.setTheme(clone(user.theme))
          }
        }
      },
      async updateAddress(body: any, uuid: string) {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const data = await $fetch(`${url}/me/addresses/${uuid}`, {
            method: 'put',
            body: cloneDeep(body),
          })
          return {
            success: true,
            data: data as BpUserAddress,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            data: null,
            error: formatError(err),
          }
        }
      },
      async updatePaymentMethod(card: any): Promise<StoreResponse> {
        try {
          const url = `${this.baseUrl()}/me/payment-methods/${card.id}`
          const data = await $fetch(url, {
            method: HTTP_METHODS.PUT,
            body: {
              ...card,
            },
          })
          return {
            success: true,
            data,
            error: null,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            data: null,
            error: formatError(err),
          }
        }
      },
      async updateUser(body: any): Promise<StoreResponse> {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const data = await $fetch(`${url}/me`, {
            method: 'put',
            body,
          })
          const user = data as BpUser
          await this.setUser(user)
          return {
            error: null,
            success: true,
            data: user,
          }
        } catch (err: any) {
          const formatError = useFormatError()
          return {
            success: false,
            error: formatError(err),
            data: null,
          }
        }
      },
      async validatePasswordResetRequest(uuid: string, token: string) {
        const url = this.baseUrl()
        if (!url) {
          return defaultError
        }
        try {
          const data = await $fetch(`${url}/passwords/validations`, {
            body: {
              uuid,
              token,
            },
            method: 'post',
          })
          return { error: null, data, success: true }
        } catch (err: any) {
          const formatError = useFormatError()
          return { success: false, error: formatError(err), data: null }
        }
      },
    },
  })
